import { Badge } from "@/components/badge";
import { Button } from "@/components/button";
import { Combobox } from "@/components/combobox";
import { Label } from "@/components/label";
import { useGlobalExperimentOptions, useImportGlobalExperiment } from "@/http/api-hooks";
import { ExperimentImportMetadata, ExperimentScopeFilter, type GlobalExperimentImportResponse } from "@/http/api-types";
import { predictorTypeName } from "@/lib/predictor-types";
import { formatUTCDateTime } from "@/lib/utils";
import { isAxiosError } from "axios";
import { RefreshCcw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { toast } from "sonner";
import type { ExperimentFormValues } from "./ExperimentForm";
import { toUTCCalendarDate } from "./experiment-form-helpers";
import { EXPERIMENT_FORM_LABEL_CLASS } from "./experiment-form-styles";
import type { ExperimentFormModel } from "./use-experiment-form-model";

type GlobalExperimentImportSectionProps = {
  isEditing: boolean;
  parseTimeFromDate: (date: Date) => string;
  replacePredictionFlights: ExperimentFormModel["replacePredictionFlights"];
};

function formatScopeFilterLabel(scopeFilter: ExperimentScopeFilter) {
  switch (scopeFilter.kind) {
    case "audience_id":
      return `Audience ${scopeFilter.value}`;
    case "campaign_id":
      return `Campaign ${scopeFilter.value}`;
    case "campaign_type":
      return `Campaign Type ${scopeFilter.value}`;
    case "budget_objective": {
      const budgetObjectiveLabels: Record<string, string> = {
        performance: "Performance",
        spend_with_perf_nudging: "Auto-Spend",
        other: "Other",
      };
      return `Budget Objective: ${budgetObjectiveLabels[scopeFilter.value] ?? scopeFilter.value}`;
    }
    default:
      return `${scopeFilter.kind} ${scopeFilter.value}`;
  }
}

function buildPredictionFlightFormValues(
  payload: GlobalExperimentImportResponse,
): ExperimentFormValues["predictionFlights"] {
  const trafficAllocation = Math.round(payload.traffic_per_flight * 10000);

  return payload.prediction_flights.map(predictionFlight => ({
    predictionFlightId: predictionFlight.prediction_flight_id.toString(),
    variant: predictionFlight.variant,
    trafficAllocation,
    params: (predictionFlight.params || []).map(param => ({
      predictorTypeId: param.predictor_type_id,
      paramValue: formatImportedParamValue(param.param_value),
    })),
  }));
}

function formatImportedParamValue(value: string) {
  try {
    const parsed = JSON.parse(value);
    if (parsed && typeof parsed === "object") {
      return JSON.stringify(parsed, null, 2);
    }
  } catch {
    return value;
  }

  return value;
}

function formatLastSynced(value: string | undefined) {
  if (!value) return "Not synced yet";
  return formatUTCDateTime(value);
}

export function GlobalExperimentImportSection({
  isEditing,
  parseTimeFromDate,
  replacePredictionFlights,
}: GlobalExperimentImportSectionProps) {
  const { control, setValue } = useFormContext<ExperimentFormValues>();

  const importedGlobalExperimentId = useWatch({ control, name: "importedGlobalExperimentId" });
  const importLastSyncedAt = useWatch({ control, name: "importLastSyncedAt" });
  const scopeFilters = useWatch({ control, name: "scopeFilters" }) || [];
  const importMetadata = useWatch({ control, name: "importMetadata" });

  const [selectedGlobalExperimentId, setSelectedGlobalExperimentId] = useState(importedGlobalExperimentId || "");

  const { data: optionsData, isLoading: optionsLoading } = useGlobalExperimentOptions();
  const { trigger: importTrigger, isMutating: isImporting } = useImportGlobalExperiment();

  useEffect(() => {
    if (!isEditing) return;
    if (!importedGlobalExperimentId) return;
    setSelectedGlobalExperimentId(importedGlobalExperimentId);
  }, [importedGlobalExperimentId, isEditing]);

  const globalExperimentOptions = useMemo(
    () =>
      optionsData?.results.map(option => ({
        label: `${option.id} - ${option.name}`,
        value: option.id.toString(),
      })) || [],
    [optionsData],
  );

  const applyImport = (payload: GlobalExperimentImportResponse) => {
    // The imported dates are UTC instants: the pickers take the UTC calendar day,
    // while the time fields read the clock off the instant itself.
    const startInstant = new Date(payload.start_date);
    const endInstant = new Date(payload.end_date);

    setValue("importedGlobalExperimentId", payload.imported_global_experiment_id.toString(), { shouldDirty: true });
    setValue("importLastSyncedAt", payload.import_last_synced_at, { shouldDirty: true });
    setValue("scopeFilters", payload.scope_filters || [], { shouldDirty: true });
    setValue("importMetadata", payload.import_metadata || null, { shouldDirty: true });
    setValue("name", payload.name || "", { shouldDirty: true });
    setValue("startDate", toUTCCalendarDate(payload.start_date), { shouldDirty: true });
    setValue("startTime", parseTimeFromDate(startInstant), { shouldDirty: true });
    setValue("endDate", toUTCCalendarDate(payload.end_date), { shouldDirty: true });
    setValue("endTime", parseTimeFromDate(endInstant), { shouldDirty: true });
    replacePredictionFlights(buildPredictionFlightFormValues(payload));
    setSelectedGlobalExperimentId(payload.imported_global_experiment_id.toString());
  };

  const handleImport = async (globalExperimentIdRaw: string, mode: "import" | "resync") => {
    if (!globalExperimentIdRaw) {
      toast.error("Select a global experiment first");
      return;
    }

    if (mode === "resync" && !window.confirm("Resync will overwrite imported fields with the latest source data.")) {
      return;
    }

    try {
      const payload = await importTrigger(parseInt(globalExperimentIdRaw, 10));
      applyImport(payload);
      toast.success(mode === "import" ? "Global experiment imported" : "Source resynced");
    } catch (error) {
      console.error("Failed to import global experiment", error);
      const serverMessage = isAxiosError(error) ? (error.response?.data as { error?: string })?.error : undefined;
      toast.error("Failed to import global experiment", { description: serverMessage });
    }
  };

  const predictorFlightMetadata = (importMetadata as ExperimentImportMetadata | null)?.prediction_flights || [];

  return (
    <section className="grid gap-4 rounded-lg border border-border/70 bg-muted/20 p-4">
      <div className="grid gap-1">
        <Label className={EXPERIMENT_FORM_LABEL_CLASS}>Import Global Experiment</Label>
        <p className="text-sm text-muted-foreground">
          Imports name, schedule, prediction flights, variant params, and readonly scope from the source global
          experiment.
        </p>
      </div>

      {!isEditing && (
        <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_160px]">
          <Combobox
            value={selectedGlobalExperimentId}
            setValue={setSelectedGlobalExperimentId}
            options={globalExperimentOptions}
            buttonPlaceholder="Select global experiment"
            searchPlaceholder="Search global experiments"
            notFoundMessage="No global experiments found"
            isLoading={optionsLoading}
            disabled={false}
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => handleImport(selectedGlobalExperimentId, "import")}
            disabled={isImporting || !selectedGlobalExperimentId}
          >
            {isImporting ? "Importing..." : "Import"}
          </Button>
        </div>
      )}

      {isEditing && importedGlobalExperimentId && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border bg-background p-3">
          <div className="grid gap-1">
            <div className="text-sm font-medium">
              Source: {importMetadata?.source_name || `Global Experiment ${importedGlobalExperimentId}`}
            </div>
            <div className="text-xs text-muted-foreground">
              ID {importedGlobalExperimentId} · last synced {formatLastSynced(importLastSyncedAt)}
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => handleImport(importedGlobalExperimentId, "resync")}
            disabled={isImporting}
          >
            <RefreshCcw className="mr-2 h-4 w-4" />
            {isImporting ? "Resyncing..." : "Resync From Source"}
          </Button>
        </div>
      )}

      {scopeFilters.length > 0 && (
        <div className="grid gap-2">
          <div className="text-sm font-medium">Readonly Scope</div>
          <div className="flex flex-wrap gap-2">
            {scopeFilters.map((scopeFilter, index) => (
              <Badge key={`${scopeFilter.kind}-${scopeFilter.value}-${index}`} variant="secondary">
                {formatScopeFilterLabel(scopeFilter)}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {predictorFlightMetadata.length > 0 && (
        <div className="grid gap-3">
          <div className="text-sm font-medium">Imported Predictor Sources</div>
          <div className="grid gap-3">
            {predictorFlightMetadata.map(predictionFlight => (
              <div key={predictionFlight.prediction_flight_id} className="rounded-md border bg-background p-3">
                <div className="text-sm font-medium">
                  {predictionFlight.name} · {predictionFlight.variant}
                </div>
                <div className="mt-2 grid gap-2">
                  {(predictionFlight.predictor_configs || []).map(predictorConfig => (
                    <div
                      key={`${predictionFlight.prediction_flight_id}-${predictorConfig.predictor_type}`}
                      className="grid gap-1 text-xs text-muted-foreground"
                    >
                      <div className="font-medium text-foreground">
                        {predictorTypeName(predictorConfig.predictor_type)}
                      </div>
                      <div>
                        Repo: {predictorConfig.repo?.trim() ? predictorConfig.repo : "not set"} · Branch:{" "}
                        {predictorConfig.branch?.trim() ? predictorConfig.branch : "not set"}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
